import { GENERIC_JOURNEY_NARRATIVE_TRANSLATIONS } from '../data/genericJourneyNarrativeTranslations/translations.js';
import { getJourneyExplanationTranslation } from '../data/journeyExplanationTranslations/index.js';
import { getGenericJourneyStageTitleTranslation } from '../data/genericJourneyStageTitleTranslations.js';
import { standardJourneyPrintCopy } from '../data/standardJourneyPrintCopy.js';
import { CONCEPT_PRINT_LABEL_DRAFTS } from '../data/conceptPrintLabelDrafts.js';
import { CONCEPT_PRINT_QR_LABELS } from '../data/conceptPrintQrLabels.js';
import { ADDITIONAL_LATIN_PRINT_LABELS } from '../data/conceptPrintLabelsAdditionalLatin.js';
import { ADDITIONAL_OTHER_PRINT_LABELS } from '../data/conceptPrintLabelsAdditionalOther.js';
import { EXISTING_PRINT_LABEL_COMPLETIONS } from '../data/conceptPrintLabelsExistingCompletion.js';
import { JOURNEY_INFO_SOURCE_TRANSLATION_IDS } from '../data/journeyInfoSupplementTranslations.js';
import { professionalTranslationFor } from '../data/professionalTranslationRuntime.js';
import { journeyRoleCardCopy } from './journeyRoleCardCopy.js';
import { JOURNEY_PHRASE_TRANSLATIONS } from '../data/journeyPhraseTranslations/index.js';

// This is a concept-only *local* print projection of the same bundled machine
// translation sources used in patient Journeys. It must never change Trust
// configuration, change clinical review state, or claim linguistic sign-off.
const shortText = value => typeof value === 'string' && value.trim() ? value : null;
const templateId = journey => String(journey?.templateId || journey?.id || '').replace(/^generic-/, '');
const STAGE_ID_ALIASES = Object.freeze({
  maternity: {
    pregnancy: 'during-pregnancy',
    'preparing-birth': 'preparing-for-birth',
    'labour-birth': 'labour-and-birth',
    'after-birth': 'after-birth',
  },
});
const indexed = (data, index) => Array.isArray(data) ? data[index] : data?.[String(index)];

function firstTranslatedSentence(value) {
  return String(value || '').split(/(?<=[.!?。؟।])\s*/u).filter(Boolean)[0] || null;
}
function getLocaleValue(data, path) {
  return path.split('.').reduce((node, key) => node?.[key], data);
}

export function localizeConceptPrintJourney(englishJourney, locale, resources = {}) {
  if (!englishJourney || locale === 'en') return { journey: englishJourney, untranslated: [] };
  const id = templateId(englishJourney);
  const localePack = GENERIC_JOURNEY_NARRATIVE_TRANSLATIONS[locale] || {};
  const narrative = localePack.templates?.[id] || {};
  const local = resources.hospitalJourney || {};
  const legacyId = JOURNEY_INFO_SOURCE_TRANSLATION_IDS[id];
  const localizedRecord = local.journeys?.['generic-' + id] || {};
  const legacyRecord = legacyId ? local.journeys?.[legacyId] || {} : {};
  const localeRecord = key => localizedRecord[key] ?? legacyRecord[key];
  const untranslated = [];
  const source = (value, candidate, path, locked = false) => {
    if (!value) return value;
    if (!locked && shortText(candidate)) return candidate;
    untranslated.push(path);
    return value;
  };
  const localizedTitle = localeRecord('title') || narrative.title;
  const localizedIntroduction = localeRecord('introduction') || narrative.introduction;
  const localQuestions = localeRecord('questions') || narrative.questions || [];
  const localizedLinks = localePack.commonLinks || {};
  const stages = (englishJourney.stages || []).map(stage => {
    const translation = getJourneyExplanationTranslation(locale, id, stage.id);
    const stageRow = narrative.stages?.[stage.id];
    const fallbackTitle = getGenericJourneyStageTitleTranslation(locale, id, stage.id);
    const stageAlias = STAGE_ID_ALIASES[id]?.[stage.id] || stage.id;
    const localStage = localeRecord('stages')?.[stage.id] || localeRecord('stages')?.[stageAlias];
    const locked = englishJourney.localTextOverrides?.stages?.[stage.id] || {};
    const stageTitle = Array.isArray(stageRow) ? stageRow[0] : stageRow?.title;
    const stageDescription = Array.isArray(stageRow) ? stageRow[1] : stageRow?.description;
    // Never reuse an English explanation under a translated heading. The
    // online app also omits rich English paragraphs when no locale exists.
    const explanation = translation || null;
    if (stage.explanation && !translation) untranslated.push('stages.' + stage.id + '.explanation (omitted)');
    return {
      ...stage,
      title: source(stage.title, localStage?.title || stageTitle || fallbackTitle, 'stages.' + stage.id + '.title', locked.title),
      description: source(stage.description,
        localStage?.description || stageDescription || firstTranslatedSentence(explanation?.whatHappens),
        'stages.' + stage.id + '.description', locked.description),
      explanation,
    };
  });
  const links = (englishJourney.usefulLinks || []).map(link => {
    const pair = localizedLinks[link.id] || [];
    const localLink = localeRecord('usefulLinks')?.[link.id] || {};
    const locked = englishJourney.localTextOverrides?.usefulLinks?.[link.id] || {};
    return {
      ...link,
      title: source(link.title, localLink.title || pair[0], 'usefulLinks.' + link.id + '.title', locked.title),
      description: source(link.description, localLink.description || pair[1], 'usefulLinks.' + link.id + '.description', locked.description),
    };
  });
  const getResource = (requestedLocale, namespace, path) => {
    if (requestedLocale !== locale) return null;
    const data = namespace === 'roles' ? resources.roles
      : namespace === 'patientGuides' ? resources.patientGuides : null;
    return data ? getLocaleValue(data, path) : null;
  };
  // Source Journeys frequently hold {id} only. The digital Journey resolves
  // these references with journeyRoleCardCopy and uses patientGuides summaries
  // when the roles catalogue has no description. Reuse that SAME resolver.
  const rolesLocalized = (englishJourney.roles || []).map(reference => {
    const role = typeof reference === 'string' ? { id: reference } : reference;
    const key = role?.role_key || role?.roleKey || role?.id;
    if (!key) return reference;
    const card = journeyRoleCardCopy({ ...role, role_key: key }, locale, getResource);
    if (!card.titleTranslated) untranslated.push('roles.' + key + '.title');
    if (!card.descriptionTranslated) untranslated.push('roles.' + key + '.description (omitted)');
    return {
      ...role,
      title: card.title || role.title || null,
      // Never put English canonical text under a translated role heading.
      description: card.description || '',
      conceptRoleLanguage: locale,
    };
  });
  const questions = (englishJourney.questions || []).map((question, index) =>
    source(question, indexed(localQuestions, index), 'questions.' + index));
  const safety = local.genericSafety?.[id === 'day-surgery' ? 'daySurgeryDisclaimer' : 'generalDisclaimer'];
  return {
    untranslated,
    journey: {
      ...englishJourney,
      title: source(englishJourney.title, localizedTitle, 'title', englishJourney.localTextOverrides?.title),
      introduction: source(englishJourney.introduction, localizedIntroduction, 'introduction'),
      stages, roles: rolesLocalized, questions, usefulLinks: links,
      disclaimer: source(englishJourney.disclaimer, safety, 'disclaimer'),
    },
  };
}

export function localizedConceptPrintCopy(locale, resources = {}) {
  const copy = {
    ...standardJourneyPrintCopy(locale),
    ...(CONCEPT_PRINT_QR_LABELS[locale] || CONCEPT_PRINT_QR_LABELS.en),
    ...(ADDITIONAL_LATIN_PRINT_LABELS[locale] || {}),
    ...(ADDITIONAL_OTHER_PRINT_LABELS[locale] || {}),
    ...(EXISTING_PRINT_LABEL_COMPLETIONS[locale] || {}),
    ...(CONCEPT_PRINT_LABEL_DRAFTS[locale] || {}),
  };
  if (locale === 'en') return copy;
  const h = resources.hospitalJourney || {};
  const safe = path => shortText(getLocaleValue(h, path));
  const overrides = {
    preparationChecklist: safe('ui.preparationChecklist.title'),
    whatYouMightHear: safe('ui.sections.phrases.title') || safe('ui.stage.phrasesTitle'),
    peopleYouMayMeet: safe('ui.sections.people.title'),
    questionsToConsider: safe('ui.sections.questions.title') || safe('ui.stage.questionsTitle'),
    officialHospitalInformation: safe('ui.sections.usefulInformation.title') || safe('ui.stage.informationTitle'),
    understandingReminder: safe('genericSafety.preparationReminder'),
    coverReminder: safe('genericSafety.aboutText'),
    footerAdvice: safe('genericSafety.preparationReminder'),
  };
  for (const [key, value] of Object.entries(overrides)) if (value) copy[key] = value;
  // Clinician-approved and professionally released fixed labels must have
  // priority over these concept-only machine-assisted print drafts.
  for (const key of Object.keys(copy)) {
    const reviewed = professionalTranslationFor(locale, 'standardJourneyPrint', key);
    if (reviewed) copy[key] = reviewed;
  }
  return copy;
}
export function localizedConceptPrintPhrases(locale, resources = {}) {
  const terms = resources.phrases?.phrases || {};
  if (locale === 'en') return terms;
  // Mirror i18next's after-load deep merge (overwrite=false): locale JSON
  // fields win, but missing fields are supplied by the same Journey phrases
  // used in the patient app. Never erase a complete supplement with a partial
  // locale record. Existing professional releases take priority over both.
  const supplemental = JOURNEY_PHRASE_TRANSLATIONS[locale] || {};
  const ids = new Set([...Object.keys(supplemental), ...Object.keys(terms)]);
  return Object.fromEntries([...ids].map(id => {
    const phrase = { ...(supplemental[id] || {}), ...(terms[id] || {}) };
    for (const field of ['title', 'heard', 'meaning', 'patientContext', 'ask', 'imageAlt']) {
      const reviewed = professionalTranslationFor(locale, 'whatYouMightHear', `phrases.${id}.${field}`);
      if (reviewed) phrase[field] = reviewed;
    }
    return [id, phrase];
  }));
}
export function conceptPrintFallbacks(locale, englishJourney, printed, copy, phraseKeys = [], usage = {}) {
  if (locale === 'en' || !englishJourney) return [];
  const fallback = [...(printed.untranslated || [])];
  const english = standardJourneyPrintCopy('en');
  // The PDF's machine-translation audit must count visible text, not unused
  // catalogue templates. Non-English role cards omit generic uniform prose,
  // and a named Trust never shows the English 'Local hospital' fallback.
  const nonEnglishUnusedRoleLabels = new Set(['professionalClothes', 'wearsColourGarment', 'wearsGarment']);
  for (const [key, value] of Object.entries(copy)) {
    if (nonEnglishUnusedRoleLabels.has(key)) continue;
    if (key === 'localHospital' && usage.hasTrustDisplayName) continue;
    if (value === english[key]) fallback.push('print-label.' + key);
  }
  for (const key of phraseKeys) {
    if (!printed.phrases?.[key]?.title || !printed.phrases?.[key]?.meaning)
      fallback.push('phrases.' + key + ' (English or omitted)');
  }
  return [...new Set(fallback)];
}
